import { useErrorHandler } from "@shared/errors";
import { useModal } from "@shared/modal";
import { useState } from "react";
import {
  HiOutlineCheckCircle,
  HiOutlineExclamationTriangle,
} from "react-icons/hi2";

import { Button } from "@/components-new/button";
import { FileUpload, FileUploadContainer } from "@/components-new/file-upload";
import { Spinner } from "@/components-new/spinner";
import { useMarketBasketService } from "@/features/market-baskets";
import { MarketBasketImportResult } from "@/features/market-baskets/types/market-basket";

export const ImportMarketDetailsPanel = ({
  marketBasketId,
  getDrugMarketDetails,
}: {
  marketBasketId: number;
  getDrugMarketDetails: () => void;
}) => {
  const { handleError } = useErrorHandler();
  const { uploadMarketDetailsFile } = useMarketBasketService();
  const { closeModal } = useModal();

  const [importing, setImporting] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | undefined>(undefined);
  const [result, setResult] = useState<MarketBasketImportResult | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] ?? null;
    setFile(selected);
    setFileError(undefined);
    setResult(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!file) {
      setFileError("File is required");
      return;
    }

    if (!file.name.toLowerCase().endsWith(".csv")) {
      setFileError("File must be a CSV");
      return;
    }

    setImporting(true);
    setResult(null);

    try {
      const importResult: MarketBasketImportResult = await uploadMarketDetailsFile(
        file,
        marketBasketId,
      );
      setResult(importResult);
      getDrugMarketDetails();
    } catch (ex) {
      handleError(
        ex as Error,
        {
          title: "Import Failed",
          message:
            "We encountered an unexpected error while importing Market Details. Please try again or contact an administrator.",
        },
        "error",
      );
    } finally {
      setImporting(false);
    }
  };

  const hasWarnings = (result?.warnings?.length ?? 0) > 0;

  if (result) {
    return (
      <div className="space-y-4">
        <div
          className={`flex items-start gap-3 rounded-lg p-4 ${hasWarnings ? "bg-yellow-50 ring-1 ring-yellow-200" : "bg-green-50 ring-1 ring-green-200"}`}
        >
          {hasWarnings ? (
            <HiOutlineExclamationTriangle className="mt-0.5 size-5 shrink-0 text-yellow-500" />
          ) : (
            <HiOutlineCheckCircle className="mt-0.5 size-5 shrink-0 text-green-600" />
          )}
          <div>
            <p
              className={`font-medium ${hasWarnings ? "text-yellow-800" : "text-green-800"}`}
            >
              {hasWarnings
                ? `Import completed with ${result.warnings.length} warning${result.warnings.length === 1 ? "" : "s"}`
                : "Import successful"}
            </p>
            <p
              className={`mt-0.5 text-sm ${hasWarnings ? "text-yellow-700" : "text-green-700"}`}
            >
              {result.rowsImported} row{result.rowsImported !== 1 ? "s" : ""}{" "}
              imported from <span className="font-medium">{file?.name}</span>
            </p>
          </div>
        </div>

        {hasWarnings && (
          <div>
            <p className="mb-1 text-sm font-medium text-zinc-700">
              Flagged rows are highlighted in the table — use override pricing to resolve missing WAC data.
            </p>
            <ul className="max-h-40 space-y-1 overflow-y-auto rounded-lg bg-zinc-50 p-3 text-sm text-yellow-700 ring-1 ring-zinc-200">
              {result.warnings.map((w, i) => (
                <li key={i} className="font-mono text-xs">
                  {w}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button
            outline
            onClick={() => {
              setResult(null);
              setFile(null);
            }}
          >
            Import Another
          </Button>
          <Button onClick={closeModal}>Done</Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <p className="mb-2 text-sm text-zinc-600">
        Upload a Gold Standard CSV file. All packagings in the file will be
        added or updated in this basket.
      </p>
      <form onSubmit={handleSubmit}>
        <FileUploadContainer>
          <FileUpload
            name="market-details-file"
            accept=".csv"
            onChange={handleFileChange}
            fileName={file?.name}
          />
        </FileUploadContainer>
        {fileError && <p className="mt-1 text-sm text-red-600">{fileError}</p>}
        <div className="mt-4 flex justify-end">
          <Button type="submit" disabled={importing || !file}>
            {importing ? (
              <>
                <Spinner /> Importing…
              </>
            ) : (
              "Import"
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};
